/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Nov 17 2023
 **/

import { useCallback, useEffect, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { _P, _T } from "@rfice/language";
import { useEventHandler } from "@rfice/eventhandler";

import {
	Button,
	Divider,
	Row,
	Tab,
	TabContainer,
	TabContent,
	TabItem,
	Typography
} from "@/frameworks/designStuff/components";
import { useFileManager, useSidebarActions } from "@/frameworks/core/hooks";
import { EventID, SidebarID } from "@/frameworks/core/commons";
import { useChat } from "@/frameworks/core/API";
// import { useChatRoomActions } from "@/frameworks/core/chat";
import { useChatDataStore } from "@/modules/chat/store";

import * as S from "./emotion";
import { SearchedConversation, SearchedFile, SearchedPeople } from "./components";

export default function ChatSearch() {
	const searchValue = useChatDataStore((state) => state.chat_search_text);
	const [searchText, setSearchText] = useState<string | undefined>(undefined);
	// const chatRoomActions = useChatRoomActions();
	const { getAllFolderFileList } = useFileManager();
	const set_totalChat_search_area_open = useChatDataStore((state) => state.set_totalChat_search_area_open);

	const sidebarActions = useSidebarActions();

	const handleClose = () => {
		sidebarActions.hide(SidebarID.ChatSearch);
		set_totalChat_search_area_open(false);
		//TODO. 기획에 맞춰서 그냥 채팅창이 열리는 것이 아닌 기존에 열려있었던 채팅창이 노출되어야한다.
		// if (useChatDataStore.getState().show_fullsize_chat) {
		// 	chatRoomActions.common.open();
		// }
	};

	const chatAPI = useChat();

	const {
		data: pagedConversations,
		refetch: refetchConversation,
		fetchNextPage: fetchNextConversation
	} = useInfiniteQuery({
		queryKey: ["chatConversation", searchText],
		queryFn: async ({ pageParam = undefined }) => {
			const requestParam: WebServices.Chat.Search.Request = {
				keyword: searchText || "",
				pageSize: 20,
				cursorId: pageParam
			};
			const result = (await chatAPI.searchMessages.execute(requestParam)).data;
			return {
				...result,
				nextPage: result?.content[result.content.length - 1].roomMessageId,
				hasNext: result?.hasNext ?? false
			};
		},
		refetchOnWindowFocus: false,
		cacheTime: 0,
		staleTime: 0,
		refetchOnMount: true,
		getNextPageParam: (lastPage) => lastPage.nextPage,
		enabled: typeof searchText !== "undefined"
	});

	const { data: pagedPeople, refetch: refetchPeople } = useQuery({
		queryKey: ["chatPeople", searchText],
		queryFn: async () => {
			const requestParam: WebServices.Chat.SearchUsers.Request["requestVO"] = {
				keyword: searchText || ""
			};
			return (await chatAPI.searchUser.execute(requestParam)).data;
		},
		refetchOnWindowFocus: false,
		enabled: typeof searchText !== "undefined"
	});

	const {
		data: pagedFiles,
		refetch: refetchFiles,
		fetchNextPage: fetchNextFiles
	} = useInfiniteQuery({
		queryKey: ["searched_files", searchText],
		queryFn: async ({ pageParam = 1 }) => {
			const result = await getAllFolderFileList({ searchText, pageSize: 20, page: pageParam });
			return { nextPage: pageParam + 1, ...result };
		},
		getNextPageParam: ({ last, ...rest }) => {
			if (!last) {
				return rest.nextPage;
			}
		},
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		enabled: typeof searchText !== "undefined",
		refetchInterval: 1000 * 60 * 5
	});

	const searchedConversations = pagedConversations?.pages.map((el) => el?.content || []).flat() || [];
	const searchedFiles = pagedFiles?.pages.map((el) => el?.content || []).flat() || [];
	const totalConversationCount = pagedConversations?.pages[0]?.totalCount || 0;
	const totalFileCount = pagedFiles?.pages[0]?.totalElements || 0;

	useEffect(() => {
		if (searchText === searchValue) {
			refetchConversation();
			refetchPeople();
			refetchFiles();
		} else {
			setSearchText(searchValue);
		}
	}, [searchValue]);

	useEventHandler(EventID.SEARCH_CHAT, () => {
		refetchConversation();
		refetchPeople();
		refetchFiles();
	});

	return (
		<S.ChatSearchLayout>
			<S.ChatSearchHeader>
				<Row css={{ width: "100%" }} verticalAlign="center" horizonAlign="distribute">
					<Row gap={12} verticalAlign="center">
						<Button.Icon id="close" name="Left24" size="large" filled="transparent" onClick={handleClose} />
						<Typography typoSize="Body1" typoColor="neutral_80_100">
							{_P("<span id='keyword'>'{{keyword}}'</span>의 검색 결과", { keyword: searchText })}
						</Typography>
					</Row>
					<Button.Icon name="Close24" size="medium" filled="transparent" onClick={handleClose} />
				</Row>
			</S.ChatSearchHeader>
			<Divider type="line" />
			<TabContainer activeId="conversation">
				<Tab css={{ width: "100%", padding: "0 60px" }}>
					<TabItem id="conversation">
						<Typography.p typoSize="Title3">
							{_T("대화내용 ({{count}})", {
								count: totalConversationCount < 100 ? totalConversationCount : "99+"
							})}
						</Typography.p>
					</TabItem>
					<TabItem id="file">
						<Typography.p typoSize="Title3">
							{_T("파일 ({{count}})", { count: totalFileCount < 100 ? totalFileCount : "99+" })}
						</Typography.p>
					</TabItem>
					<TabItem id="people">
						<Typography.p typoSize="Title3">
							{_T("사람 ({{count}})", { count: pagedPeople?.length })}
						</Typography.p>
					</TabItem>
				</Tab>
				<S.Body>
					<TabContent css={{ width: "100%", height: "100%" }} activeId="conversation">
						<SearchedConversation
							searchText={searchText || ""}
							conversations={searchedConversations}
							onIntersecting={useCallback(() => {
								const pageSize = pagedConversations?.pages.length || 0;
								if (pagedConversations?.pages[pageSize - 1]?.hasNext) {
									fetchNextConversation();
								}
							}, [totalConversationCount])}
						/>
					</TabContent>
					<TabContent css={{ width: "100%", height: "100%" }} activeId="file">
						<SearchedFile
							searchText={searchText || ""}
							files={searchedFiles}
							onIntersecting={useCallback(() => {
								const pageSize = pagedFiles?.pages.length || 0;
								if (!pagedFiles?.pages[pageSize - 1]?.last) {
									fetchNextFiles();
								}
							}, [totalFileCount])}
						/>
					</TabContent>
					<TabContent css={{ width: "100%", height: "100%" }} activeId="people">
						<SearchedPeople searchText={searchText || ""} people={pagedPeople} />
					</TabContent>
				</S.Body>
			</TabContainer>
		</S.ChatSearchLayout>
	);
}
